import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, TablePagination } from "@material-ui/core";
import { GetApp } from "@material-ui/icons";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import { Loading, ErrorBox, Pane, HFlex, VFlex, Text } from "shared";
import { isUsMarket } from "utils/market";
import { useReferralTracking, useUpdateReferral, useBulkUpdateReferrals, exportReferralsCsv } from "./queries";
import { ReferralTrackingParams } from "./queries/types";
import { PAGE_SIZE_OPTIONS } from "./constants";
import ReferralTrackingTable from "./components/ReferralTrackingTable";
import ReferralTrackingFilters from "./components/ReferralTrackingFilters";
import ReferralDetailDrawer from "./components/ReferralDetailDrawer";
import BulkEditModal from "./components/BulkEditModal";
import { Referral } from "./queries/types";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2, 3, 4),
  },
  paper: {
    overflow: "hidden",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  bulkBar: {
    padding: theme.spacing(1, 2),
    backgroundColor: "rgba(80, 150, 232, 0.08)",
    borderTop: "1px solid rgba(80, 150, 232, 0.2)",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
}));

const getDateRangeFromPreset = (preset: string): { start: string; end: string } | null => {
  const now = new Date();
  const fmt = (d: Date) => d.toISOString().split("T")[0];
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (preset) {
    case "today":
      return { start: fmt(today), end: fmt(today) };
    case "this_week": {
      const weekStart = new Date(today);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      return { start: fmt(weekStart), end: fmt(today) };
    }
    case "last_30": {
      const d = new Date(today);
      d.setDate(d.getDate() - 30);
      return { start: fmt(d), end: fmt(today) };
    }
    case "last_90": {
      const d = new Date(today);
      d.setDate(d.getDate() - 90);
      return { start: fmt(d), end: fmt(today) };
    }
    default:
      return null;
  }
};

const ReferralTrackingPage: React.VFC = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(50);
  const [sortBy, setSortBy] = useState("referral_date");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [datePreset, setDatePreset] = useState("");
  const [dateRangeStart, setDateRangeStart] = useState("");
  const [dateRangeEnd, setDateRangeEnd] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [drawerReferral, setDrawerReferral] = useState<Referral | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Compute date range from preset or custom
  const dateRange =
    datePreset === "custom" && dateRangeStart && dateRangeEnd
      ? { start: dateRangeStart, end: dateRangeEnd }
      : datePreset
        ? getDateRangeFromPreset(datePreset)
        : null;

  const queryParams: ReferralTrackingParams = {
    offset: page * perPage,
    limit: perPage,
    sort_by: sortBy,
    sort_order: sortDirection,
    search: debouncedSearch || undefined,
    filters: Object.keys(filters).length > 0 ? filters : undefined,
    date_from: dateRange?.start,
    date_to: dateRange?.end,
  };

  const { data, isLoading, error } = useReferralTracking(queryParams);
  const updateReferral = useUpdateReferral();
  const bulkUpdate = useBulkUpdateReferrals();

  // Keep drawer referral in sync with refetched data
  useEffect(() => {
    if (drawerReferral && data?.items) {
      const updated = data.items.find((r) => r.id === drawerReferral.id);
      if (updated) setDrawerReferral(updated);
    }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateReferralRef = useRef(updateReferral.mutate);
  updateReferralRef.current = updateReferral.mutate;
  const bulkUpdateRef = useRef(bulkUpdate.mutate);
  bulkUpdateRef.current = bulkUpdate.mutate;

  const handleSortChange = useCallback(
    (field: string) => {
      if (field === sortBy) {
        setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        setSortBy(field);
        setSortDirection("asc");
      }
      setPage(0);
    },
    [sortBy]
  );

  const handleCellSave = useCallback(
    (id: number, field: string, value: string | null) => {
      updateReferralRef.current({ id, field, value });
    },
    []
  );

  const handleBulkConfirm = useCallback(
    (fields: Record<string, string | null>) => {
      bulkUpdateRef.current({ ids: Array.from(selectedIds), fields });
    },
    [selectedIds]
  );

  const handleExport = () => {
    exportReferralsCsv(queryParams);
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleDateRangeChange = (start: string, end: string) => {
    setDateRangeStart(start);
    setDateRangeEnd(end);
    setPage(0);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const handleFiltersChange = (newFilters: Record<string, string[]>) => {
    setFilters(newFilters);
    setPage(0);
  };

  const handleDatePresetChange = (preset: string) => {
    setDatePreset(preset);
    setPage(0);
  };

  if (isUsMarket) return null;

  return (
    <VFlex gap={2} className={classes.root}>
      <HFlex justifyContent="space-between" alignItems="flex-start">
        <VFlex gap={0.5}>
          <Text bold large>{t("referral_tracking.title")}</Text>
          <Text secondary tiny>{t("referral_tracking.subtitle")}</Text>
        </VFlex>
        <Button variant="outlined" size="small" startIcon={<GetApp />} onClick={handleExport}>
          {t("referral_tracking.export_csv")}
        </Button>
      </HFlex>

      <Pane zeroBorderRadius p={0} className={classes.paper}>
        <ReferralTrackingFilters
          search={search}
          onSearchChange={handleSearchChange}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          dateRangeStart={dateRangeStart}
          dateRangeEnd={dateRangeEnd}
          datePreset={datePreset}
          onDatePresetChange={handleDatePresetChange}
          onDateRangeChange={handleDateRangeChange}
        />

        {error && <ErrorBox error={error} centered verbose mt={2} mb={2} />}

        {isLoading ? (
          <Loading mt={8} mb={8} />
        ) : (
          <ReferralTrackingTable
            referrals={data?.items || []}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            onCellSave={handleCellSave}
            onRowClick={setDrawerReferral}
          />
        )}

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <Box className={classes.bulkBar}>
            <Text tiny bold>{selectedIds.size} {t("referral_tracking.selected")}</Text>
            <Button size="small" variant="contained" color="primary" onClick={() => setBulkEditOpen(true)}>
              {t("referral_tracking.bulk_edit")}
            </Button>
            <Button size="small" onClick={() => setSelectedIds(new Set())}>
              {t("referral_tracking.clear_selection")}
            </Button>
          </Box>
        )}

        <Box className={classes.footer}>
          <TablePagination
            component="div"
            count={data?.total_count || 0}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={perPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          />
        </Box>
      </Pane>

      <BulkEditModal
        open={bulkEditOpen}
        selectedCount={selectedIds.size}
        onClose={() => setBulkEditOpen(false)}
        onConfirm={handleBulkConfirm}
      />

      <ReferralDetailDrawer
        referral={drawerReferral}
        onClose={() => setDrawerReferral(null)}
        onCellSave={handleCellSave}
      />
    </VFlex>
  );
};

export default ReferralTrackingPage;
